import * as fromReducer from './parts-appointment.reducer';
import * as fromAdapter from './parts-appointment.adapter';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { FeatureKeys } from './../app.reducer';
import { IAvailabilityOptions, IAsyncData, IAvailability, AppointmentStatus, IVehicle, IDealerInfo } from '@signal/asp-data-commons';
import { ITransport } from '@signal/asp-data-commons/lib/interfaces/general-setttings';
import { IChecklistRequest } from '@signal/asp-data-commons/lib/interfaces/appointment';
import {dealerDetailsData} from '../dealer/dealer.selectors';
import { time } from "../settings/settings.selectors";

export const selectPartsAppointmentFeature = createFeatureSelector<fromReducer.PartsAppointmentState>(
    FeatureKeys.PARTS_APPOINTMENT
);

export const stateDetails = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => state
);

export const editDetails = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => state.editDetails
);

/* Used in learning appointment to check whether edit details got editmode or undefined */
export const isEditModeInEditDetails = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => state?.editDetails?.isEditMode?true:false
);

export const appointmentDetails = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => state.appointment
);

export const deliveryAppointmentDetails = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => fromAdapter.deliveryAppointmentDetails(state.appointment)
);

export const testDriveAppointmentDetails = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => fromAdapter.testdriveAppointmentDetails(state.appointment)
);

export const inquiryAppointmentDetails = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => fromAdapter.inquiryVehicleAppointmentDetails(state.appointment)
);

export const appointmentStatus = createSelector( 
    appointmentDetails,
    (appointment: fromReducer.IPartsAppointmentForm) => (appointment.appointmentStatus === AppointmentStatus.SCHEDULED)
)

/* To select vehicle from edit learning appointment form */
export const editAppointmentVehicle = createSelector(
    appointmentDetails,
    (appointment: fromReducer.IPartsAppointmentForm) => [appointment.vehicle]
)

/* To select the isOwnVehicle from the vehicle form */
export const selectVehicleFormIsOwnVehicle = createSelector(
    appointmentDetails,
    (appointment: fromReducer.IPartsAppointmentForm) => appointment.vehicle.isOwnVehicle
  );

export const getAppointmentStartDate = createSelector(
    appointmentDetails,
    (appointment: fromReducer.IPartsAppointmentForm) => appointment.availability.appointmentStartDate
);

export const availabilityOptions = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => state.availabilityOptions
);

export const getAvailabilityOptions = createSelector(
    availabilityOptions,
    (availabilityOptions: IAsyncData<IAvailabilityOptions>) => availabilityOptions.data
);

export const getAvailabilityOptionsLoading = createSelector(
    availabilityOptions,
    (availabilityOptions: IAsyncData<IAvailabilityOptions>) => availabilityOptions.loading
);

export const availability = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => state.availability
);

export const getAvailability = createSelector(
    availability,
    (availability: IAsyncData<IAvailability>) => availability.data
);

export const getAvailabilityLoading = createSelector(
    availability,
    (availability: IAsyncData<IAvailability>) => availability.loading
);

export const getAvailabilityFormAdvisorId = createSelector(
    appointmentDetails,
    (appointmentDetails: fromReducer.IPartsAppointmentForm) => appointmentDetails.availability.advisorId
);

export const getTransport = createSelector(
    getAvailabilityOptions,
    (availabilityOptions: IAvailabilityOptions) => availabilityOptions.transport
);

export const getTransportOptions = createSelector(
    getTransport,
    (transport: ITransport) => transport?.transportOptions
);

export const getPMAZipCodes = createSelector(
    getTransport,
    (transport: ITransport) => transport?.includedRemoteZipCodes
);

export const getYearList = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => fromAdapter.getYearList()
);

export const getSeriesList = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState, props: { model?: string, year: number }) =>
        fromAdapter.getSeriesList(state.seriesDetails, props.year)
);

export const getTrimList = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState, props: { model: string, year: number }) =>
        fromAdapter.getTrimList(state.seriesDetails, props.year, props.model)
);

export const getExteriorColorsList = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => state.exteriorColors.data
);

export const getMileage = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState, props: { model: string, year: number }) =>
        fromAdapter.getMileage(state.seriesDetails, props.year, props.model)
);

export const getPrice = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState, props: { model: string, year: number }) =>
        fromAdapter.getPrice(state.seriesDetails, props.year, props.model)
);

export const getVehicleDetails = createSelector(
    getYearList,
    getSeriesList,
    getTrimList,
    getExteriorColorsList,
    getMileage,
    getPrice,
    (yearList, seriesList, trimList, exteriorColors, mileage, price, props: { model: string, year: number }) =>
        fromAdapter.getVehicleDetails(yearList, seriesList, trimList, exteriorColors, mileage, price)
);

export const getVehicleDetailsForVin = createSelector(
    selectPartsAppointmentFeature,
    getExteriorColorsList,
    appointmentDetails,
    getMileage,
    getPrice,
    (state: fromReducer.PartsAppointmentState, exteriorColors, appointment: fromReducer.IPartsAppointmentForm, mileage, price, props: { model: string, year: number }) => (
        fromAdapter.getVehicleDetailsForVinTestDrive( state,exteriorColors,appointment?.vehicle,mileage,price)
    )
)

export const getOtherVehiclesSeriesList = (props: { model?: string, year: number }) => createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) =>
        fromAdapter.getSeriesList(state.seriesDetails, props.year)
);

export const getOtherVehiclesTrimList = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState, props: { model: string, year: number }) =>
        fromAdapter.getTrimList(state.seriesDetails, props.year, props.model)
);

export const getAvailabilityDetails = createSelector(
    appointmentDetails,
    (appointment: fromReducer.IPartsAppointmentForm) => 
    ({
        transport: appointment.transportationTypeCode, 
        advisorId: appointment.availability.advisorId,
        appointmentStartDate: appointment.availability.appointmentStartDate,
        timeSlotId: appointment.availability.timeSlotId,
        languageId: appointment.availability.languageId
    })
);

export const getAppointmentAvailability = createSelector(
    appointmentDetails,
    (appointment: fromReducer.IPartsAppointmentForm) => appointment.availability
)

export const selectVehicleForm = createSelector(
    appointmentDetails,
    (appointment: fromReducer.IPartsAppointmentForm) => appointment.vehicle
);

export const selectChecklistForm = createSelector(
    appointmentDetails,
    (appointment: fromReducer.IPartsAppointmentForm) => appointment.checklistRequest
);

/* --------------- Footer summary --------------------------------- */
/* To select the appointment vehicle details and form vehicle summary */
export const selectAppointmentVehicleSummary = createSelector(
    selectVehicleForm,
    (vehicleForm: fromReducer.IPartsAppointmentForm['vehicle']) => vehicleForm ? fromAdapter.appointmentVehicleFormToSummaryProjector(vehicleForm) : []
);

/* To select the appointment checklist requests and form checklist request summary */
export const appointmentChecklistRequestFormToSummaryProjector = createSelector(
    selectChecklistForm,
    time,
    (checklistDetails: IChecklistRequest[],time:any) =>
        fromAdapter.appointmentChecklistRequestFormToSummaryProjector(checklistDetails,time)
);

/* To select the appointment details and form footer summary array */
export const selectSalesAppointmentFooterSummaries = createSelector(
    selectAppointmentVehicleSummary,
    appointmentChecklistRequestFormToSummaryProjector,
    (vehicleSummary: fromReducer.IFooterSummary[], checklistRequestSummary: fromReducer.IFooterSummary[]) => [...vehicleSummary, ...checklistRequestSummary]
);

/* To select the currentStep from the sales appointment state */
export const selectCurrentStep = createSelector(
    selectPartsAppointmentFeature,
    (state: fromReducer.PartsAppointmentState) => state.currentStep
);

export const selectVehicleFormImageUrl = createSelector(
    selectVehicleForm,
    (vehicleForm: fromReducer.IPartsAppointmentForm['vehicle']) => vehicleForm?.imageUrl
);

export const selectVehicleFormMakeModelYearText = createSelector(
    selectVehicleForm,
    (vehicleForm: fromReducer.IPartsAppointmentForm['vehicle']) => vehicleForm ? fromAdapter.vehicleToMakeModelYearTextProjector(vehicleForm) : ''
);

export const selectAppointmentFormAndLookUpData = createSelector(
    appointmentDetails,
    getAvailabilityOptions,
    (appointmentForm: fromReducer.IPartsAppointmentForm, availabilityOptions: IAvailabilityOptions) =>
        ({ appointmentForm, availabilityOptions })
);

export const selectChecklistDurationForAvailability = createSelector(
    selectChecklistForm,
    (checklistRequest: IChecklistRequest[]) => fromAdapter.getChecklistDurationForAvailability(checklistRequest)
)

/* To select appointment form and derive detailMap */
export const selectAppointmentFormDetailMap = createSelector(
    selectAppointmentFormAndLookUpData,
    dealerDetailsData,
    (data: { appointmentForm: fromReducer.IPartsAppointmentForm, availabilityOptions: IAvailabilityOptions }, dealerDetails: IDealerInfo) =>
        fromAdapter.appointmentFormToDetailMap(data, dealerDetails)
);



